import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover } from 'antd';
import { Icon, Props, TextArea } from '@common/components';
import EmojiPicker, {
  EmojiClickData,
  SuggestionMode,
} from 'emoji-picker-react';

const RenderEmojiPicker = ({
  onEmojiClick,
}: {
  onEmojiClick: (emoji: EmojiClickData) => void;
}) => (
  <EmojiPicker
    height={350}
    width={370}
    onEmojiClick={onEmojiClick}
    suggestedEmojisMode={SuggestionMode.RECENT}
  />
);

export const TextAreaEmojiPicker = (
  props: Omit<Props, 'onChange'> & {
    value?: string;
    onChange?: (value: string) => void;
  }
) => {
  const { value, onChange } = props;
  const [isHover, setIsHover] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [openPopover, setOpenPopover] = useState(true);

  useEffect(() => {
    setOpenPopover(false);

    const timeout = setTimeout(() => {
      setLoaded(true);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  // we can't use state inside the onEmojiClick function
  const valueRef = useRef<string>(value || '');

  useEffect(() => {
    valueRef.current = value || '';
  }, [value]);

  const onEmojiClick = useCallback((emoji: EmojiClickData) => {
    const emojiCode = emoji.emoji;
    const textArea = document.querySelector<HTMLTextAreaElement>(
      '.wk-textarea-emoji-picker textarea'
    );
    const cursorPosition = textArea?.selectionStart;

    const currentValue = valueRef.current;

    if (!cursorPosition || !textArea) {
      onChange?.(`${currentValue || ''}${emojiCode}`);
      return;
    }

    const textBeforeCursorPosition = currentValue?.substring(0, cursorPosition);
    const textAfterCursorPosition = currentValue?.substring(
      cursorPosition,
      currentValue.length
    );
    const emojiCodeLength = emojiCode.length;
    const textWithEmoji = `${textBeforeCursorPosition}${emojiCode}${textAfterCursorPosition}`;

    onChange?.(textWithEmoji);
    textArea.focus();
    textArea.setSelectionRange(
      cursorPosition + emojiCodeLength,
      cursorPosition + emojiCodeLength
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = useMemo(() => {
    return <RenderEmojiPicker onEmojiClick={onEmojiClick} />;
  }, [onEmojiClick]);

  return (
    <div className="wk-textarea-emoji-picker">
      <TextArea
        {...props}
        onChange={(e) => onChange?.(e.target.value)}
        style={{ paddingRight: 30 }}
      />
      <div className="emoji-button">
        <Popover
          placement="topRight"
          content={content}
          open={openPopover}
          overlayStyle={{ visibility: loaded ? 'visible' : 'hidden' }}
          align={{ offset: [27.5, 10] }}
          trigger="click"
          onOpenChange={(open) => {
            setOpenPopover(open);
            if (open) {
              const textArea = document.querySelector<HTMLTextAreaElement>(
                '.wk-textarea-emoji-picker textarea'
              );
              textArea?.focus();
            }
          }}
          arrowPointAtCenter={true}
        >
          <div
            style={{ cursor: 'pointer', padding: 5 }}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <Icon
              icon={isHover ? 'faceLaughSquint' : 'faceSmile'}
              color={isHover ? 'elm' : 'midnightLighter'}
              kind="light"
            />
          </div>
        </Popover>
      </div>
      <style jsx>{`
        :global(.ant-popover-inner-content) {
          padding: 0;
        }
        :global(aside.EmojiPickerReact.epr-main) {
          border-width: 0;
        }
        :global(.wk-textarea-emoji-picker .emoji-button) {
          position: absolute;
          right: 0;
          top: 0;
        }
      `}</style>
    </div>
  );
};
